import { getNotifications } from '../api/notification';
import { getSubdomain } from '../utils/getSubdomain';
import { AppNotification, sortNotificationsByNewest } from '../utils/notificationDisplay';
import { hasLearnerSession } from '../auth/learner/session';
import {
  LEARNER_SESSION_CLEARED_EVENT,
  LEARNER_SESSION_UPDATED_EVENT,
} from '../auth/learner/constants';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';

interface NotificationContextProps {
  notifications: AppNotification[];
  addNotification: (notification: AppNotification) => void;
  removeNotification: (bulk_session_id: string) => void;
  getConnection: () => Socket | null;
  setNotifications: React.Dispatch<React.SetStateAction<AppNotification[]>>;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(
  undefined
);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider'
    );
  }
  return context;
};

type IPropsType = {
  children: React.ReactNode;
};

const isLearnerActive = () =>
  hasLearnerSession() ||
  (typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/learner'));

export const NotificationProvider = ({ children }: IPropsType) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [learnerActive, setLearnerActive] = useState(isLearnerActive);
  const userId = localStorage.getItem('userId');

  const refreshLearnerFlag = useCallback(() => {
    setLearnerActive(isLearnerActive());
  }, []);

  useEffect(() => {
    refreshLearnerFlag();
    window.addEventListener(LEARNER_SESSION_UPDATED_EVENT, refreshLearnerFlag);
    window.addEventListener(LEARNER_SESSION_CLEARED_EVENT, refreshLearnerFlag);
    window.addEventListener('storage', refreshLearnerFlag);
    return () => {
      window.removeEventListener(
        LEARNER_SESSION_UPDATED_EVENT,
        refreshLearnerFlag
      );
      window.removeEventListener(
        LEARNER_SESSION_CLEARED_EVENT,
        refreshLearnerFlag
      );
      window.removeEventListener('storage', refreshLearnerFlag);
    };
  }, [refreshLearnerFlag]);

  useEffect(() => {
    if (learnerActive) {
      setNotifications([]);
      return;
    }

    const fetchNotifications = async () => {
      try {
        const response = await getNotifications();
        setNotifications(sortNotificationsByNewest(response.data.data ?? []));
      } catch (error: any) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchNotifications();
    
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "default") {
        Notification.requestPermission();
      }
    }
  }, [learnerActive]);

  useEffect(() => {
    if (learnerActive) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }
      return;
    }

    if (socket) return;

    const staffEmail = (localStorage.getItem('email') || '').trim()
    const staffId = (localStorage.getItem('userId') || userId || '').trim()
    const newSocket = io(`${process.env.REACT_APP_BASE_URL}`, {
      query: { email: staffEmail || staffId, subdomain: getSubdomain() },
      transports: ['websocket'],
    });

    newSocket.on('connect', () => {
      console.log('Connected to WebSocket');
    });
    newSocket.on('notification', event => {
      console.log('noti', event);
      setNotifications(prev =>
        sortNotificationsByNewest([
          {
            id: event.id ?? event.bulk_session_id,
            title: event.title,
            message: event.message ?? event.body ?? '',
            bulk_session_id: event.bulk_session_id,
            generation_job_id: event.generation_job_id,
            subject_title: event.subject_title,
            topic: event.topic,
            chapter: event.chapter,
            question_count: event.question_count,
            uploaded_at: event.uploaded_at,
            created_at: event.created_at,
            is_read: false,
          },
          ...prev.filter(
            item =>
              item.id !== (event.id ?? event.bulk_session_id) &&
              item.bulk_session_id !== event.bulk_session_id
          ),
        ])
      );
      
      // Native browser notification when tab is inactive
      if (
        typeof window !== "undefined" && 
        "Notification" in window && 
        Notification.permission === "granted" && 
        document.hidden
      ) {
        new Notification(event.title, { body: event.message || event.body || "You have a new notification." });
      }
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from WebSocket');
    });

    newSocket.on('connect_error', error => {
      console.error('WebSocket Connection Error:', error.message);
    });

    newSocket.on('connect_failed', () => {
      console.error('WebSocket Connection Failed');
    });

    newSocket.on('error', error => {
      console.error('WebSocket Error:', error);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnerActive]);

  const addNotification = (notification: AppNotification) => {
    if (isLearnerActive()) return;
    setNotifications(prev =>
      sortNotificationsByNewest([
        notification,
        ...prev.filter(
          item =>
            item.id !== notification.id &&
            item.bulk_session_id !== notification.bulk_session_id
        ),
      ])
    );
  };

  const removeNotification = (bulk_session_id: string) => {
    setNotifications(prev =>
      prev.filter(n => n.bulk_session_id !== bulk_session_id)
    );
  };

  const getConnection = () => (learnerActive ? null : socket);

  return (
    <NotificationContext.Provider
      value={{
        notifications: learnerActive ? [] : notifications,
        addNotification,
        removeNotification,
        getConnection,
        setNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
